import { CommentsStateModel } from './state/comments-state.model';
import { ClientRequestItemActivityViewModel } from '../../models';
import { Comment, CommentSectionItem, MessageEntityType } from './comments.model';
import { ClientRequestModel, MASKED_ITEM_RESPONSE_VALUE, RequestDocument } from '@aiwyn/api';
import { uniq } from 'lodash';

/**
 * Helper function to check if a comment or any of its replies are unread
 */
export function hasUnreadMessages(comment: Comment): boolean {
  if (comment.isUnread) {
    return true;
  }
  if (comment.replies && comment.replies.length > 0) {
    return comment.replies.some((reply) => hasUnreadMessages(reply));
  }
  return false;
}

export function mergeCommentsWithActivity(
  state: CommentsStateModel,
  activity: ClientRequestItemActivityViewModel[],
  selectedRequest: ClientRequestModel,
  restrictedItems: RequestDocument[],
): Record<string, CommentSectionItem[]> {
  const response: Record<string, CommentSectionItem[]> = {};

  const activities: Record<string, CommentSectionItem[]> = activity.reduce((acum, activityItem) => {
    const userName = activityItem.userName || 'Unknown User';
    const nameParts = userName.split(' ');
    const firstName = nameParts[0] || 'Unknown';
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

    const requestItem = selectedRequest.requestItems.find((rItem) => rItem.id === activityItem.itemId);
    const isRestrictedItem = Boolean(restrictedItems.find((restricted) => restricted.id === requestItem?.id));

    const key = `${MessageEntityType.CLIENT_REQUEST_ITEM}:${activityItem.itemId}`;

    acum[key] = acum[key] ?? [];
    acum[key].push({
      id: null, // activities are not comments
      isUnread: false,
      isComment: false,
      isMasked: requestItem?.restricted,
      entityId: activityItem.itemId,
      author: {
        id: activityItem.userId?.toString(),
        firstName,
        lastName,
        isStaff: true, // Activity is typically from staff
      },
      text: activityItem.rejectionNotes,
      timestamp: new Date(activityItem.rejectedAt),
      context: activityItem.itemName ?? requestItem.name,
      replies: [],
      entityType: MessageEntityType.CLIENT_REQUEST_ITEM,
      flaggedFiles: uniq(
        (activityItem.files ?? [])
          .filter((file) => file.is_flagged)
          .map((file) => (isRestrictedItem ? MASKED_ITEM_RESPONSE_VALUE : file.name)),
      ),
    });

    return acum;
  }, {});

  for (const [key, comments] of Object.entries(state.commentsByEntity)) {
    const itemActivities = activities[key] ?? [];

    const commentsWithActivity: CommentSectionItem[] = [
      ...comments.map((c) => ({
        ...c,
        replies: c.replies.toSorted((a, b) => a.timestamp.getTime() - b.timestamp.getTime()),
        isComment: true,
      })),
      ...itemActivities.map((item) => {
        item.context = item.context || comments[0]?.context;
        return item;
      }),
    ];

    commentsWithActivity.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    response[key] = commentsWithActivity;
  }

  for (const [key, comments] of Object.entries(activities)) {
    if (!response[key]) {
      response[key] = comments;
    }
  }

  return response;
}

export function getFirstAndLastNameFromFullName(fullName: string): { firstName: string; lastName: string } {
  if (!fullName) {
    return null;
  }
  const firstName = fullName.split(' ')[0] ?? null;
  let lastName = fullName.split(' ').pop() ?? null;
  if (firstName.includes('@') && lastName === firstName) {
    lastName = '';
  }
  return { firstName, lastName };
}
